import {authFetch} from "../../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

const SCOPE_ALIASES = {
    NOTE: ['NOTE'],
    RECALL: ['RECALL', 'RECALL_CARD', 'RECALLCARD'],
};

function getScopeCandidates(scope) {
    return SCOPE_ALIASES[scope] ?? [scope];
}

function normalizeFolder(folder) {
    if (!folder) return null;

    return {
        id: folder.id,
        userId: folder.userId ?? folder.user_id ?? null,
        name: folder.name,
        parentFolderId: folder.parentFolderId ?? folder.parent_folder_id ?? null,
        sortOrder: Number(folder.sortOrder ?? folder.sort_order ?? 0),
        scope: folder.scope,
        createdAt: folder.createdAt ?? folder.created_at ?? null,
        updatedAt: folder.updatedAt ?? folder.updated_at ?? null,
    };
}

export function matchesFolderScope(folderScope, scope) {
    if (!scope) return true;
    return getScopeCandidates(scope).includes(folderScope);
}

export async function fetchFolderById(authToken, folderId) {
    if (!authToken || !folderId) return null;

    const response = await authFetch(`${API_URL}/api/folder/${folderId}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw new Error('Failed to fetch folder');
    }

    return normalizeFolder(await response.json());
}

export async function fetchFolders(authToken) {
    if (!authToken) return [];

    const response = await authFetch(`${API_URL}/api/folders`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (response.status === 404) {
        return [];
    }

    if (!response.ok) {
        throw new Error('Failed to fetch folders');
    }

    const payload = await response.json();
    const folders = Array.isArray(payload) ? payload : payload?.items ?? [];

    return folders.map(normalizeFolder).filter(Boolean);
}

async function postFolder(authToken, folderData, scopeValue) {
    return authFetch(`${API_URL}/api/folder`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: folderData.name,
            parent_folder_id: folderData.parentFolderId ?? null,
            sort_order: String(folderData.sortOrder ?? 1),
            scope: scopeValue,
        }),
    });
}

export async function createFolder(authToken, folderData) {
    if (!authToken) {
        throw new Error('Missing auth token');
    }

    const scopeCandidates = getScopeCandidates(folderData.scope);
    let lastError = null;
    const finalScopeCandidate = scopeCandidates[scopeCandidates.length - 1];

    for (const scopeValue of scopeCandidates) {
        const response = await postFolder(authToken, folderData, scopeValue);

        if (response.ok) {
            return normalizeFolder(await response.json());
        }

        if (![400, 422].includes(response.status) || scopeValue === finalScopeCandidate) {
            lastError = new Error('Failed to create folder');
            break;
        }
    }

    throw lastError ?? new Error('Failed to create folder');
}

export async function deleteFolder(authToken, folderId) {
    if (!authToken || !folderId) {
        throw new Error('Missing required folder delete data');
    }

    const response = await authFetch(`${API_URL}/api/folder/${folderId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error('Failed to delete folder');
    }
}

